import React, { useEffect, useState } from 'react';
import { CheckCircle, ChevronDown, ChevronUp } from 'lucide-react';
import { BeforeAfterSlider } from './BeforeAfterSlider';
import { CTABanner } from './CTABanner';
export interface FAQ {
  question: string;
  answer: string;
}
export interface BeforeAfterImage {
  before: string;
  after: string;
  label: string;
}
export interface ServicePageTemplateProps {
  serviceName: string;
  heroImage: string;
  heroDescription: string;
  description: string;
  benefits: string[];
  whySoftWash: string;
  faqs: FAQ[];
  beforeAfterImages: BeforeAfterImage[];
}
export function ServicePageTemplate({
  serviceName,
  heroImage,
  heroDescription,
  description,
  benefits,
  whySoftWash,
  faqs,
  beforeAfterImages
}: ServicePageTemplateProps) {
  const [openFaqIndex, setOpenFaqIndex] = useState<number | null>(0);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  return <div className="w-full" data-id="element-159">
      {/* Hero Banner */}
      <section className="relative pt-32 pb-20 md:pt-40 md:pb-28 overflow-hidden" data-id="element-160">
        <div className="absolute inset-0 bg-cover bg-center z-0" style={{
        backgroundImage: `url("${heroImage}")`
      }} data-id="element-161"></div>
        <div className="absolute inset-0 bg-navy/80 z-10" data-id="element-162"></div>
        <div className="relative z-20 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center" data-id="element-163">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-montserrat font-black text-white mb-6" data-id="element-164">
            {serviceName}
          </h1>
          <p className="text-xl text-cream max-w-3xl mx-auto" data-id="element-165">
            {heroDescription}
          </p>
        </div>
      </section>

      {/* Service Description */}
      <section className="py-16 md:py-24 bg-white" data-id="element-166">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-167">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center" data-id="element-168">
            <div data-id="element-169">
              <h2 className="text-3xl font-montserrat font-bold text-navy mb-6" data-id="element-170">
                Professional {serviceName}
              </h2>
              <p className="text-gray-600 text-lg leading-relaxed mb-6" data-id="element-171">
                {description}
              </p>
              <div className="bg-cream/30 p-6 rounded-xl border border-cream" data-id="element-172">
                <h3 className="text-xl font-montserrat font-bold text-navy mb-3" data-id="element-173">
                  Why Choose Soft Washing?
                </h3>
                <p className="text-gray-700" data-id="element-174">{whySoftWash}</p>
              </div>
            </div>
            <div className="rounded-2xl overflow-hidden shadow-xl h-[400px]" data-id="element-175">
              <img src={heroImage} alt={serviceName} className="w-full h-full object-cover" data-id="element-176" />
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-16 bg-gray-50" data-id="element-177">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-178">
          <div className="text-center mb-12" data-id="element-179">
            <h2 className="text-3xl font-montserrat font-bold text-navy mb-4" data-id="element-180">
              Benefits of Our Service
            </h2>
            <div className="w-24 h-1 bg-light-blue mx-auto rounded-full" data-id="element-181"></div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-4xl mx-auto" data-id="element-182">
            {benefits.map((benefit, index) => <div key={index} className="flex items-start bg-white p-4 rounded-lg shadow-sm border border-gray-100" data-id="element-183">
                <CheckCircle className="w-6 h-6 text-light-blue shrink-0 mr-3 mt-0.5" data-id="element-184" />
                <span className="text-gray-700 font-medium" data-id="element-185">{benefit}</span>
              </div>)}
          </div>
        </div>
      </section>

      {/* Before & After */}
      {beforeAfterImages.length > 0 && <section className="py-16 md:py-24 bg-white" data-id="element-186">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-187">
            <div className="text-center mb-12" data-id="element-188">
              <h2 className="text-3xl font-montserrat font-bold text-navy mb-4" data-id="element-189">
                See the Difference
              </h2>
              <p className="text-gray-600" data-id="element-190">
                Drag the slider to compare before and after our{' '}
                {serviceName.toLowerCase()}.
              </p>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-12" data-id="element-191">
              {beforeAfterImages.map((img, index) => <BeforeAfterSlider key={index} beforeImage={img.before} afterImage={img.after} label={img.label} data-id="element-192" />)}
            </div>
          </div>
        </section>}

      {/* FAQ */}
      {faqs.length > 0 && <section className="py-16 bg-gray-50" data-id="element-193">
          <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-194">
            <div className="text-center mb-12" data-id="element-195">
              <h2 className="text-3xl font-montserrat font-bold text-navy mb-4" data-id="element-196">
                Frequently Asked Questions
              </h2>
              <div className="w-24 h-1 bg-light-blue mx-auto rounded-full" data-id="element-197"></div>
            </div>
            <div className="space-y-4" data-id="element-198">
              {faqs.map((faq, index) => <div key={index} className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden" data-id="element-199">
                  <button className="w-full px-6 py-4 text-left flex justify-between items-center focus:outline-none" onClick={() => setOpenFaqIndex(openFaqIndex === index ? null : index)} data-id="element-200">
                    <span className="font-montserrat font-semibold text-navy pr-4" data-id="element-201">
                      {faq.question}
                    </span>
                    {openFaqIndex === index ? <ChevronUp className="w-5 h-5 text-light-blue shrink-0" data-id="element-202" /> : <ChevronDown className="w-5 h-5 text-gray-400 shrink-0" data-id="element-203" />}
                  </button>
                  {openFaqIndex === index && <div className="px-6 pb-4 text-gray-600 border-t border-gray-100 pt-4" data-id="element-204">
                      {faq.answer}
                    </div>}
                </div>)}
            </div>
          </div>
        </section>}

      {/* CTA */}
      <CTABanner title={`Schedule Your ${serviceName} Today`} subtitle="Contact us for a free, no-obligation quote and let us restore your property's beauty." data-id="element-205" />
    </div>;
}